import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useDispatch } from 'react-redux';
import {
  UI_OPERATION_STATUS_REQUEST,
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_SUCCESS,
  setOperationStatus,
} from '@uncinc/react-drupal-core';
import {
  Container,
  FormikWrapper,
  FormEmail as EmailField,
  FormTextField as TextField,
  FormCheckbox as CheckboxField,
} from '@uncinc/uncinc-react-kitchen-sink';
import * as Yup from 'yup';
import { FormikHelpers } from 'formik';
import { MINIMUM_PASSWORD_CHARACTER_COUNT } from '../../config/constants';
import { userRegisterAsNursePractitioner } from '../../store/actions/User';
import useClasses from '../../hooks/useClasses';
import useOperationStatus from '../../hooks/useOperationStatus';
import Page from '../../containers/Page';
import { Button, PasswordField } from '../../components';
import useToast from '../../hooks/useToast';

import './style.scss';

const RegisterNursePractitioner: React.FC = () => {
  const { t } = useTranslation();
  const { buildClass, joinClasses } = useClasses('RegisterNursePractitioner');
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const registerOperationStatus = useOperationStatus('user', 'register');
  const toast = useToast('error');

  const translatePageContent = useCallback((key: string) => t(`pages.register_nurse_practitioner.${key}`), [t]);
  const translateFormContent = useCallback((key: string) => translatePageContent(`fields.${key}`), [translatePageContent]);

  const [showSuccesMessage, setShowSuccesMessage] = useState(false);

  useEffect(() => {
    if (registerOperationStatus === UI_OPERATION_STATUS_ERROR) {
      toast.show(translatePageContent('error'));
    }

    if (registerOperationStatus === UI_OPERATION_STATUS_SUCCESS) {
      setShowSuccesMessage(true);
    }

    if (registerOperationStatus !== UI_OPERATION_STATUS_REQUEST) {
      // Consume the operation status so the toast won't show again.
      dispatch(setOperationStatus('user', 'register', null));
    }
  }, [registerOperationStatus, toast, dispatch, translatePageContent]);

  const validationSchema = Yup.object({
    initials: Yup.string()
      .trim()
      .required(translateFormContent('initials.validation.required')),
    lastName: Yup.string()
      .trim()
      .required(translateFormContent('last_name.validation.required')),
    function: Yup.string()
      .trim()
      .required(translateFormContent('function.validation.required')),
    generalPractitioner: Yup.string()
      .trim()
      .required(translateFormContent('general_practitioner.validation.required')),
    generalPractitionerAddress: Yup.string()
      .trim()
      .required(translateFormContent('general_practitioner_address.validation.required')),
    generalPractitionerPostcode: Yup.string()
      .trim()
      .matches(/^[1-9][0-9]{3}\s*(?!sa|sd|ss)[a-z]{2}$/i, translateFormContent('general_practitioner_postcode.validation.invalid'))
      .required(translateFormContent('general_practitioner_postcode.validation.required')),
    generalPractitionerCity: Yup.string()
      .trim()
      .required(translateFormContent('general_practitioner_city.validation.required')),
    generalPractitionerTel: Yup.string()
      .trim()
      .required(translateFormContent('general_practitioner_tel.validation.required')),
    email: Yup.string()
      .email(translateFormContent('email.validation.email'))
      .required(translateFormContent('email.validation.required')),
    password: Yup.string()
      .min(MINIMUM_PASSWORD_CHARACTER_COUNT, t('pages.reset_password.fields.password.validation.min', { count: MINIMUM_PASSWORD_CHARACTER_COUNT }))
      .matches(/^(?=.*\d)(?=.*[a-zA-Z]).*$/, t('pages.reset_password.fields.password.validation.invalid'))
      .required(translateFormContent('password.validation.required')),
    passwordConfirm: Yup.string()
      .oneOf([Yup.ref('password'), null], translateFormContent('password_confirm.validation.no_match'))
      .required(translateFormContent('password_confirm.validation.required')),
    wantsWelcomeKit: Yup.boolean(),
  });

  type FormValues = Yup.InferType<typeof validationSchema>;

  const initialValues: FormValues = {
    initials: '',
    lastName: '',
    function: '',
    generalPractitioner: '',
    generalPractitionerAddress: '',
    generalPractitionerPostcode: '',
    generalPractitionerCity: '',
    generalPractitionerTel: '',
    email: '',
    password: '',
    passwordConfirm: '',
    wantsWelcomeKit: false,
  };

  const handleSubmit = async (
    { email, password, ...values }: FormValues,
    formikHelpers: FormikHelpers<FormValues>,
  ) => {
    await dispatch(userRegisterAsNursePractitioner({
      email,
      password,
      field_initials: values.initials,
      field_family_name: values.lastName,
      field_function: values.function,
      field_general_practitioner: values.generalPractitioner,
      field_address: values.generalPractitionerAddress,
      field_postal_code: values.generalPractitionerPostcode,
      field_locality: values.generalPractitionerCity,
      field_telephone_number: values.generalPractitionerTel,
      field_welcome_package: values.wantsWelcomeKit ? 1 : 0,
    }));

    formikHelpers.setSubmitting(false);
  };

  return (
    <Page className={buildClass()} hideMenuMain title={translatePageContent('title')}>
      <Container>
        <div className="row">
          <div className="col-md-8 offset-md-2 col-lg-6 offset-lg-3 py-8 py-lg-12">
            {showSuccesMessage ? (
              <>
                <h1 className={buildClass('title')}>
                  {translatePageContent('confirmation.title')}
                </h1>
                <p className={buildClass('lead')}>{translatePageContent('confirmation.content')}</p>
              </>
            ) : (
              <>
                <h1 className={buildClass('title')}>
                  {translatePageContent('title')}
                </h1>
                <p className={buildClass('lead')}>{translatePageContent('lead')}</p>

                <FormikWrapper
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  validateOnChange={false}
                  validateOnBlur={false}
                >
                  <TextField
                    fieldName="initials"
                    title={translateFormContent('initials.label')}
                    placeholder={translateFormContent('initials.placeholder')}
                  />

                  <TextField
                    fieldName="lastName"
                    title={translateFormContent('last_name.label')}
                    placeholder={translateFormContent('last_name.placeholder')}
                  />

                  <TextField
                    fieldName="function"
                    title={translateFormContent('function.label')}
                    placeholder={translateFormContent('function.placeholder')}
                  />

                  <TextField
                    fieldName="generalPractitioner"
                    title={translateFormContent('general_practitioner.label')}
                    placeholder={translateFormContent(
                      'general_practitioner.placeholder',
                    )}
                  />

                  <TextField
                    fieldName="generalPractitionerAddress"
                    title={translateFormContent('general_practitioner_address.label')}
                    placeholder={translateFormContent(
                      'general_practitioner_address.placeholder',
                    )}
                  />

                  <TextField
                    fieldName="generalPractitionerPostcode"
                    title={translateFormContent(
                      'general_practitioner_postcode.label',
                    )}
                    placeholder={translateFormContent(
                      'general_practitioner_postcode.placeholder',
                    )}
                  />

                  <TextField
                    fieldName="generalPractitionerCity"
                    title={translateFormContent('general_practitioner_city.label')}
                    placeholder={translateFormContent(
                      'general_practitioner_city.placeholder',
                    )}
                  />

                  <TextField
                    fieldName="generalPractitionerTel"
                    type="tel"
                    title={translateFormContent('general_practitioner_tel.label')}
                    placeholder={translateFormContent(
                      'general_practitioner_tel.placeholder',
                    )}
                  />

                  <EmailField
                    fieldName="email"
                    title={translateFormContent('email.label')}
                    placeholder={translateFormContent('email.placeholder')}
                  />

                  <PasswordField
                    fieldName="password"
                    title={translateFormContent('password.label')}
                  />

                  <PasswordField
                    fieldName="passwordConfirm"
                    title={translateFormContent('password_confirm.label')}
                  />

                  <CheckboxField
                    fieldName="wantsWelcomeKit"
                    wrapperClassName={joinClasses('Checkbox--transparent', buildClass('wants-welcome-kit'))}
                    title={translateFormContent('wants_welcome_kit.label')}
                  />

                  <Button
                    className={buildClass('submit')}
                    htmlType="submit"
                    buttonState={registerOperationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
                  >
                    {translatePageContent('submit')}
                  </Button>
                </FormikWrapper>
              </>
            )}
          </div>
        </div>
      </Container>
    </Page>
  );
};

export default RegisterNursePractitioner;
